import { FALLBACK_ERROR } from '../components/ApiErrorMessage';

/**
 * Extracts a user-facing error message from an API/axios error.
 * Handles common response shapes: array, errors[], message.
 * Returns FALLBACK_ERROR when no message can be extracted.
 */
export const extractApiErrorMessage = (error: unknown): string => {
  const axiosError = error as { response?: { data?: unknown } };
  const raw = axiosError?.response?.data;
  let message = FALLBACK_ERROR;
  if (raw) {
    if (Array.isArray(raw)) {
      message = (raw[0] as { message?: string })?.message ?? message;
    } else if (Array.isArray((raw as { errors?: Array<{ message?: string }> })?.errors) && (raw as { errors: Array<{ message?: string }> }).errors.length > 0) {
      message = (raw as { errors: Array<{ message?: string }> }).errors[0]?.message ?? message;
    } else if (typeof (raw as { message?: string })?.message === 'string' && (raw as { message: string }).message) {
      message = (raw as { message: string }).message;
    } else if (typeof raw === 'string' && raw) {
      message = raw;
    }
  }
  return message;
};

const toNonBlankText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/**
 * The API's own message from an error response body. The backend puts it in
 * `errors[0].message` (e.g. "Invalid eurl"); some endpoints still use a
 * top-level `message`. `errors[0].message` wins when it has text — when it is
 * missing, null or blank, `message` is used instead. Returns undefined when
 * neither has text, so callers add their own fallback with `??`.
 */
export const getApiErrorMessage = (responseBody: unknown): string | undefined => {
  if (!responseBody || typeof responseBody !== 'object') {
    return undefined;
  }
  const { errors, message } = responseBody as { errors?: unknown; message?: unknown };
  const firstError: unknown = Array.isArray(errors) ? errors[0] : undefined;
  const firstErrorMessage =
    firstError && typeof firstError === 'object'
      ? toNonBlankText((firstError as { message?: unknown }).message)
      : undefined;
  return firstErrorMessage ?? toNonBlankText(message);
};
